import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { appService } from "../../App/app.service";
import { fetchFirstLevelTeamNames, fetchDefaultTeamFilter } from "../rccUtils";

export default function useFreshNewQualifiedPipelineData() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.app.user || {});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvedTeamName, setResolvedTeamName] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [firstLevelTeamNames, setFirstLevelTeamNames] = useState(null);
  const [defaultTeamFilter, setDefaultTeamFilter] = useState(null);
  const lastFiltersRef = useRef();

  useEffect(() => {
    let isMounted = true;
    fetchFirstLevelTeamNames()
      .then((names) => {
        if (!isMounted) return;
        setFirstLevelTeamNames(Array.isArray(names) ? names : []);
      })
      .catch(() => {
        if (!isMounted) return;
        setFirstLevelTeamNames([]);
      });
    fetchDefaultTeamFilter()
      .then((teams) => {
        if (!isMounted) return;
        setDefaultTeamFilter(Array.isArray(teams) ? teams : []);
      })
      .catch(() => {
        if (!isMounted) return;
        setDefaultTeamFilter([]);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const isTeamFiltersReady =
    firstLevelTeamNames !== null && defaultTeamFilter !== null;

  const resolvedFilters = useMemo(() => {
    const fromDefault =
      Array.isArray(defaultTeamFilter) && defaultTeamFilter.length > 0
        ? String(defaultTeamFilter[0])
        : null;
    const fromFirst =
      Array.isArray(firstLevelTeamNames) && firstLevelTeamNames.length > 0
        ? String(firstLevelTeamNames[0])
        : null;
    const teamName =
      fromDefault && fromDefault.toLowerCase() !== "all"
        ? fromDefault
        : fromFirst;
    setResolvedTeamName(teamName || null);
    return teamName ? [{ name: "team", value: [teamName] }] : [];
  }, [defaultTeamFilter, firstLevelTeamNames]);

  useEffect(() => {
    setAppliedFilters(resolvedFilters);
    dispatch({ type: "current_selected_filters", filters: resolvedFilters });
  }, [resolvedFilters, dispatch]);

  useEffect(() => {
    if (!user?.company || !isTeamFiltersReady) {
      setLoading(true);
      return;
    }
    const filtersStr = JSON.stringify(resolvedFilters);
    if (lastFiltersRef.current === filtersStr && data !== null) return;
    lastFiltersRef.current = filtersStr;
    setLoading(true);
    setError(null);
    appService
      .getSQLCreatedData(resolvedFilters, "SQL")
      .then((json) => {
        const message = json?.message || json;
        setData(message);
        setLoading(false);
      })
      .catch((err) => {
        const msg =
          typeof err === "object"
            ? err?.message || "Something went wrong"
            : err;
        setError(msg);
        setLoading(false);
      });
  }, [resolvedFilters, isTeamFiltersReady, user?.company]);

  return {
    data,
    loading,
    error,
    selectedTeamName: resolvedTeamName,
    appliedFilters,
  };
}
